import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../firebase/auth.tsx";
import "../App.css";
import styles from "../styles/headquarterList.module.css";
import ConfirmDeleteHQModal from "./modals/confirmDeleteHQModal.tsx";
import ChangeHQDataModal from "./modals/changeHQDataModal.tsx";
import ConfirmHideModal from "./modals/confirmHideModal.tsx";
import ConfirmPermissionModal from "./modals/confirmPermissionModal.tsx";

import { authedFetch } from "./helpers/requests.tsx";

import { useTranslation } from "react-i18next";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string;
  permission?: boolean;
  scenarioId?: string;
  isHidden?: boolean;
}

const HeadquarterList = ({ headquarters, setHeadquarters }) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { userId } = useAuth();
  const [isLoadingHQ, setIsLoadingHQ] = useState(false);
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonScenarios, setComingSoonScenarios] = useState<any[]>([]);
  const [selectedHQId, setSelectedHQId] = useState("");
  const [selectedHQName, setSelectedHQName] = useState("");
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [editingHQ, setEditingHQ] = useState<{
    [key: string]: boolean;
  }>({});
  const [showChangeDataModal, setShowChangeDataModal] = useState(false);

  const [visibility, setVisibility] = useState({});
  const [showConfirmHideModal, setShowConfirmHideModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] =
    useState(false);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [selectedScenarioName, setSelectedScenarioName] = useState("");
  const [selectedTraining, setSelectedTraining] = useState<SelectedTraining>(
    {}
  );

  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingHQ(true);
      await getHeadquarters();
      setIsLoadingHQ(false);
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      setHeadquarters([]);
      setSelectedHQId("");
      setSelectedHQName("");
      setEditingHQ({});
      setTrainingsList([]);
    };
  }, [setHeadquarters]);

  const fetchHQTrainingsList = async (headquarterId: string) => {
    setIsLoadingTrainings(true);
    try {
      const trainings = await authedFetch(
        `api/requests/getTrainingsListWithPermissions`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: headquarterId,
            entityType: "headquarter",
            showAll: true,
          }),
        }
      );

      setTrainingsList(trainings);

      // Initialize visibility state
      const initialVisibility = {};
      trainings.forEach((training) => {
        initialVisibility[training.scenarioId] =
          initialVisibility[training.scenarioId] || training.isHidden;
      });
      setVisibility(initialVisibility);
    } catch (error) {
      console.error("Error get headquarter trainings list:", error);
      throw new Error("An error occurred while get headquarter trainings list");
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const fetchComingSoonScenarios = async (headquarterId: string) => {
    setIsLoadingTrainings(true);

    try {
      const scenarios = await authedFetch(
        `api/requests/getComingSoonScenarios`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: headquarterId,
            entityType: "headquarter",
            showAll: true,
          }),
        }
      );

      setComingSoonScenarios(scenarios);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        scenarios.forEach((scenario) => {
          updatedVisibility[scenario.id] = scenario.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      console.error("Error get coming soon scenarios:", error);
      throw new Error("An error occurred while get coming soon scenarios");
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const getHeadquarters = async () => {
    try {
      const data = await authedFetch(`api/requests/getHeadquarters`, {
        method: "POST",
      });
      setHeadquarters(data);
    } catch (error) {
      console.error("Error getting headquarter:", error);
      throw new Error("An error occurred while get headquarter");
    }
  };

  const updateHQPermission = async (
    headquarterId: string,
    trainingId: string,
    isChecked: boolean,
    isHidden: boolean = false
  ) => {
    try {
      await authedFetch(`api/requests/updateTrainingPermission`, {
        method: "POST",
        body: JSON.stringify({
          entityId: headquarterId,
          entityType: "headquarter",
          trainingId: trainingId,
          isChecked: isChecked,
          isHidden: isHidden,
        }),
      });
    } catch (error) {
      console.error("Error update headquarter training permission:", error);
      throw new Error(
        "An error occurred while update headquarter training permission"
      );
    }
  };

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleViewClick = async (
    headquarterId: string,
    headquarterName: string
  ) => {
    setIsLoadingHQ(true);
    navigate(`/groupList/headquarter/${headquarterId}`);
  };

  const handleEditClick = async (
    headquarterId: string,
    headquarterName: string
  ) => {
    setEditingHQ((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[headquarterId] = true;
      return newState;
    });

    setSelectedHQId(headquarterId);
    setSelectedHQName(headquarterName);

    await fetchHQTrainingsList(headquarterId);
    await fetchComingSoonScenarios(headquarterId);
  };

  const handleDeleteHQ = () => {
    setHeadquarters((prevHQ) =>
      prevHQ.filter((headquarter) => headquarter.id !== selectedHQId)
    );
    setSelectedHQId("");
    setSelectedHQName("");
    setEditingHQ({});
    setTrainingsList([]);
    setShowConfirmDeleteModal(false);
  };

  const handleChangeData = async (newHQName: string) => {
    setHeadquarters((prevHQ) =>
      prevHQ.map((headquarter) => {
        if (headquarter.id === selectedHQId) {
          return {
            ...headquarter,
            name: newHQName,
          };
        }
        return headquarter;
      })
    );

    setSelectedHQName(newHQName);
    setShowChangeDataModal(false);
  };

  const renderConfirmDeleteHQModal = () => {
    return (
      <ConfirmDeleteHQModal
        setShowConfirmDeleteHQModal={setShowConfirmDeleteModal}
        headquarterId={selectedHQId}
        headquarterName={selectedHQName}
        handleDeleteHQ={handleDeleteHQ}
      />
    );
  };

  const renderChangeDataModal = () => {
    return (
      <ChangeHQDataModal
        setShowChangeDataModal={setShowChangeDataModal}
        headquarterId={selectedHQId}
        headquarterName={selectedHQName}
        handleChangeData={handleChangeData}
      />
    );
  };

  const renderConfirmPermissionModal = () => {
    return (
      <ConfirmPermissionModal
        setShowConfirmPermissionModal={setShowConfirmPermissionModal}
        trainingId={selectedTraining.id}
        trainingName={selectedTraining.name}
        targetId={selectedTraining.entityId}
        targetName={selectedHQName}
        permission={selectedTraining.permission}
        handlePermissionChange={handlePermissionChange}
      />
    );
  };

  const renderConfirmHideModal = () => {
    const isHidden = visibility[selectedScenario];
    if (isHidden) handleHideClick(selectedScenario);
    else {
      return (
        <ConfirmHideModal
          setShowConfirmHideModal={setShowConfirmHideModal}
          scenario={selectedScenario}
          scenarioName={selectedScenarioName}
          headquarterView={true}
          handleHideClick={handleHideClick}
        />
      );
    }
  };

  const handlePermissionChange = async (
    trainingId: string,
    targetId: string,
    permission: boolean
  ) => {
    // do in paralel as .net is too slow with Firestore requests
    updateHQPermission(targetId, trainingId, permission);
    setTrainingsList(
      trainingsList.map((training) => {
        if (training.id === trainingId) {
          return { ...training, permission: permission };
        }

        return training;
      })
    );
    setSelectedTraining({});
  };

  const handleHideClick = (scenarioId) => {
    setShowConfirmHideModal(false);
    const isHidden = visibility[scenarioId];
    if (
      comingSoonScenarios.some(
        (comingSoonScenario) => comingSoonScenario.id === scenarioId
      )
    ) {
      // Update permissions to false for scenario
      const updatedComingSoonScenarios = comingSoonScenarios.map(
        (comingSoonScenario) => {
          if (comingSoonScenario.id === scenarioId) {
            updateHQPermission(
              selectedHQId,
              comingSoonScenario.id,
              true,
              !isHidden
            );
            return { ...comingSoonScenario, isHidden: !isHidden };
          }
          return comingSoonScenario;
        }
      );

      setComingSoonScenarios(updatedComingSoonScenarios);
    } else {
      // Update permissions to false for all trainings under this scenario
      const updatedTrainings = trainingsList.map((training) => {
        if (training.scenarioId === scenarioId) {
          updateHQPermission(training.entityId, training.id, false, !isHidden);
          return { ...training, permission: false, isHidden: !isHidden };
        }
        return training;
      });

      setTrainingsList(updatedTrainings);
    }

    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [scenarioId]: !prevVisibility[scenarioId],
    }));
  };

  const groupTrainingsByScenario = (trainings) => {
    const groupedTrainings = {};
    trainings.forEach((training) => {
      const {
        scenarioId,
        scenarioName,
        name,
        id,
        permission,
        entityId,
        isHidden,
      } = training;
      if (!groupedTrainings[scenarioId]) {
        groupedTrainings[scenarioId] = {
          scenarioName: scenarioName,
          trainings: [],
        };
      }
      groupedTrainings[scenarioId].trainings.push({
        id: id,
        entityId: entityId,
        name: name,
        permission: permission,
        isHidden: isHidden,
      });
    });

    return groupedTrainings;
  };

  const renderGroupedTrainings = (groupedTrainings: {
    [scenarioId: string]: {
      scenarioName: string;
      trainings: {
        id: string;
        entityId: string;
        name: string;
        permission: boolean;
        isHidden: boolean;
      }[];
    };
  }) => {
    return (
      <>
        {Object.entries(groupedTrainings).map(
          ([scenarioId, { scenarioName, trainings }]) => {
            const isAnyTrainingHidden = trainings.some(
              (training) => training.isHidden
            );
            return (
              <div key={scenarioId} className={styles.trainingsGroup}>
                <label
                  className={styles.trainingsGroupTitle}
                  style={{ color: isAnyTrainingHidden ? "gray" : "#183a68" }}
                >
                  {scenarioName}
                </label>
                <span
                  onClick={() => {
                    setSelectedScenario(scenarioId);
                    setSelectedScenarioName(scenarioName);
                    setShowConfirmHideModal(true);
                  }}
                  style={{
                    marginLeft: "1vh",
                    fontSize: "0.7em",
                    cursor: "pointer",
                  }}
                >
                  {isAnyTrainingHidden ? t("show") : t("hide")}
                </span>
                <ul>
                  {trainings.map((training) => (
                    <li
                      key={training.id}
                      className="form-check custom-checkbox"
                      style={{ color: training.isHidden ? "gray" : "#212529" }}
                    >
                      <label className="custom-checkbox-label">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="agreeCheckbox"
                          checked={training.permission}
                          //disabled={training.isHidden}
                          onChange={(e) => {
                            setSelectedTraining((prevInfo) => ({
                              ...prevInfo,
                              id: training.id,
                              name: training.name,
                              entityId: training.entityId,
                              permission: training.permission,
                            }));
                            setShowConfirmPermissionModal(true);
                          }}
                        />
                        <span className="checkmark"></span>
                        <span className="form-check-label">
                          {training.name}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
        )}
        {comingSoonScenarios.length > 0 && (
          <label
            className={styles.trainingsGroupTitle}
            style={{ color: "#183a68" }}
          >
            {t("Coming_soon")}:
          </label>
        )}
        {comingSoonScenarios.map(({ id, name, isHidden }) => {
          return (
            <div key={id} className={styles.trainingsGroup}>
              <label
                className={styles.trainingsGroupTitle}
                style={{ color: isHidden ? "gray" : "#212529" }}
              >
                {name}
              </label>
              <span
                onClick={() => {
                  setSelectedScenario(id);
                  setSelectedScenarioName(name);
                  setShowConfirmHideModal(true);
                }}
                style={{
                  marginLeft: "1vh",
                  fontSize: "0.7em",
                  cursor: "pointer",
                }}
              >
                {isHidden ? t("show") : t("hide")}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className={styles.adminContainer}>
      {isLoadingHQ ? (
        <p className={styles.loading}>{t("Loading...")}</p>
      ) : (
        <>
          {/* Left side */}
          <div className={styles.leftContainer}>
            {/* Separate table header - don't find other way to avoid overlaping header on scroll */}
            <div className={styles.tableHeader}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t("Headquarter")}</th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div className={styles.tableContent}>
              <div className={`container-fluid ${styles.containerFluid}`}>
                <table className="table">
                  <tbody>
                    {headquarters.map((headquarter: any, index: number) => (
                      <tr
                        key={headquarter.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#dbdfe7" : "transparent",
                        }}
                      >
                        <td>
                          {" "}
                          <img
                            src="/images/Icon_HQ.png"
                            style={{
                              width: "2.5vh",
                              minWidth: "10px",
                              marginLeft: "0.3vh",
                              marginRight: "1vh",
                            }}
                          />
                          {headquarter.name}
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              handleViewClick(headquarter.id, headquarter.name)
                            }
                            style={{
                              backgroundColor: "#183a68",
                              borderColor: "#183a68",
                            }}
                          >
                            {t("View")}
                          </button>
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              handleEditClick(headquarter.id, headquarter.name)
                            }
                            style={{
                              backgroundColor: editingHQ[headquarter.id]
                                ? "#404040"
                                : "#183a68",
                              borderColor: editingHQ[headquarter.id]
                                ? "#404040"
                                : "#183a68",
                            }}
                          >
                            {t("Details")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Right side */}
          <div className={styles.rightContainer}>
            {selectedHQId ? (
              <>
                <div className={styles.containerHeaderWrapper}>
                  <div className={styles.containerHeader}>
                    <div className={styles.userDataWrapper}>
                      <div className={styles.userDataName}>
                        <img
                          src="/images/Icon_HQ.png"
                          style={{
                            width: "5vh",
                            minWidth: "25px",
                            marginRight: "15px",
                          }}
                        />
                        {selectedHQName}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className="dropdown" ref={dropdownRef}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={toggleDropdown}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#183a68",
                          borderColor: "#183a68",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                      </button>
                      {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowChangeDataModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Edit.png" />
                            {t("Edit_Headquarter")}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowConfirmDeleteModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Delete.png" />
                            {t("Delete_Headquarter")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <hr />

                  <p className={styles.containerHeaderTitle}>
                    {t("Training_permission")}
                  </p>
                  <p className={styles.containerHeaderDescription}>
                    {t(
                      "Set_Headquarter_access_to_these_trainings_in_the_VR_application"
                    )}
                  </p>
                </div>

                <div className={`${styles.trainingsContainer} thin-scrollbar`}>
                  {isLoadingTrainings ? (
                    <p>{t("Loading...")}</p>
                  ) : (
                    renderGroupedTrainings(
                      groupTrainingsByScenario(trainingsList)
                    )
                  )}
                </div>
              </>
            ) : (
              <p className={styles.selectUser}>
                {t("Select_Headquarter_to_edit")}
              </p>
            )}
          </div>
        </>
      )}
      <div className={styles.adminContainer}>
        {(showConfirmDeleteModal && renderConfirmDeleteHQModal()) ||
          (showChangeDataModal && renderChangeDataModal()) ||
          (showConfirmHideModal && renderConfirmHideModal()) ||
          (showConfirmPermissionModal && renderConfirmPermissionModal())}
      </div>
    </div>
  );
};
export default HeadquarterList;
